(function () {
  if (window.customElements && customElements.get('bundle-builder')) return;

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      this.moneyFormat = this.dataset.moneyFormat || '{{amount}} EGP';
      this.cartUrl = this.dataset.cartUrl || '/cart';
      this.heading = this.dataset.heading || '';
      this.chooserEyebrow = this.querySelector('[data-counter]')
        ? this.querySelector('[data-counter]').textContent
        : '';

      this.chooseView = this.querySelector('[data-view="choose"]');
      this.buildView = this.querySelector('[data-view="build"]');
      this.stages = Array.prototype.slice.call(this.querySelectorAll('[data-stage]'));

      this.bar = this.querySelector('[data-bar]');
      this.slotHost = this.querySelector('[data-slots]');
      this.rail = this.querySelector('[data-rail]');
      this.cta = this.querySelector('[data-cta]');
      this.ctaLabel = this.querySelector('[data-cta-label]');
      this.ctaPrice = this.querySelector('[data-now]');
      this.thumbSig = null;
      this.backBtn = this.querySelector('[data-back]');
      this.closeBtn = this.querySelector('[data-close]');

      this.lb = this.querySelector('[data-lb]');
      this.lbTrack = this.querySelector('[data-lb-track]');
      this.lbDots = this.querySelector('[data-lb-dots]');
      this.lbCap = this.querySelector('[data-lb-cap]');
      this.lbPrev = this.querySelector('[data-lb-prev]');
      this.lbNext = this.querySelector('[data-lb-next]');
      this.inter = this.querySelector('[data-inter]');
      this.interText = this.querySelector('[data-inter-text]');

      this.fallbackNote = this.dataset.fallbackNote || '';
      this.interMs = parseInt(this.dataset.interMs, 10) || 3000;
      this.lottieUrl = this.dataset.lottie || '';
      // Keyed rather than a single flag: unlocking a tier and buying are two
      // separate moments, and each should fire exactly once.
      this.fired = {};
      this.shots = [];
      this.shot = 0;
      this.opener = null;

      // Picks live per bundle, so backing out to the chooser and returning
      // finds the bundle exactly as it was left.
      this.state = this.stages.map(function (stage) {
        return Array.prototype.slice
          .call(stage.querySelectorAll('[data-step]'))
          .map(function () {
            return [];
          });
      });

      this.bundle = -1;
      this.index = 0;
      this.steps = [];

      this.addEventListener('click', this.onClick.bind(this));
      if (this.cta) this.cta.addEventListener('click', this.onCta.bind(this));
      if (this.backBtn) this.backBtn.addEventListener('click', this.back.bind(this));
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.close.bind(this));

      this.onKey = this.onKey.bind(this);
      document.addEventListener('keydown', this.onKey);
      this.bindSwipe();

      // One bundle needs no chooser: a first tap that has only one answer is
      // a tap not worth asking for.
      if (this.stages.length === 1) {
        this.enter(0, false);
      } else {
        this.toChooser(false);
      }

      // Let the sheet paint off-screen for one frame, then slide it up, so the
      // entrance animation actually runs instead of being skipped.
      requestAnimationFrame(
        function () {
          requestAnimationFrame(
            function () {
              this.classList.add('is-open');
            }.bind(this),
          );
        }.bind(this),
      );
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
    }

    /* ------------------------------------------------------------- state */

    // Picks for the bundle in play. Empty when the chooser is up, so every
    // caller can read it without first asking which screen we are on.
    picks() {
      return this.bundle > -1 ? this.state[this.bundle] : [];
    }

    stage() {
      return this.bundle > -1 ? this.stages[this.bundle] : null;
    }

    // Base rate, or the better one once enough pieces are in.
    rate() {
      const stage = this.stage();
      if (!stage) return 0;
      const base = parseFloat(stage.dataset.percent) || 0;
      const at = parseInt(stage.dataset.bonusQty, 10) || 0;
      const bonus = parseFloat(stage.dataset.bonusPercent) || 0;
      return at > 0 && this.count() >= at && bonus > base ? bonus : base;
    }

    count() {
      return this.picks().reduce(function (sum, list) {
        return sum + list.length;
      }, 0);
    }

    need(index) {
      const panel = this.steps[index];
      return panel ? parseInt(panel.dataset.min, 10) || 1 : 1;
    }

    allow(index) {
      const panel = this.steps[index];
      const max = panel ? parseInt(panel.dataset.max, 10) || 1 : 1;
      return Math.max(max, this.need(index));
    }

    done(index) {
      const list = this.picks()[index];
      return !!list && list.length >= this.need(index);
    }

    complete() {
      const self = this;
      return this.steps.length > 0 && this.steps.every(function (panel, i) {
        return self.done(i);
      });
    }

    /* ------------------------------------------------------------ routing */

    onClick(event) {
      const choice = event.target.closest('[data-choose]');
      if (choice && this.contains(choice)) {
        event.preventDefault();
        this.enter(parseInt(choice.dataset.choose, 10), true);
        return;
      }

      const shutter = event.target.closest('[data-gallery]');
      if (shutter && this.contains(shutter)) {
        event.preventDefault();
        this.openGallery(shutter);
        return;
      }

      if (event.target.closest('[data-lb-close]')) return this.closeGallery();
      if (event.target.closest('[data-lb-prev]')) return this.showShot(this.shot - 1);
      if (event.target.closest('[data-lb-next]')) return this.showShot(this.shot + 1);

      const dot = event.target.closest('[data-lb-dot]');
      if (dot) return this.showShot(parseInt(dot.dataset.lbDot, 10));

      // Clicking the dimmed area around the photo closes, as shoppers expect.
      if (event.target === this.lb) return this.closeGallery();

      // Choosing a colour or size only arms the card's button; it no longer
      // puts the piece in the bundle by itself.
      const option = event.target.closest('[data-choice]');
      if (option && this.contains(option)) {
        event.preventDefault();
        this.chooseOption(option);
        return;
      }

      const button = event.target.closest('[data-variant]');
      if (!button || !this.contains(button)) return;

      const panel = button.closest('[data-step]');
      if (!panel) return;

      const index = this.steps.indexOf(panel);
      if (index < 0) return;

      event.preventDefault();
      this.toggle(index, button, panel);
    }

    chooseOption(btn) {
      const card = btn.closest('.bb__cardwrap');
      const panel = btn.closest('[data-step]');
      if (!card || !panel) return;
      const index = this.steps.indexOf(panel);

      card.querySelectorAll('[data-choice]').forEach(function (other) {
        other.setAttribute('aria-pressed', other === btn ? 'true' : 'false');
      });

      const value = card.querySelector('[data-optvalue]');
      if (value) value.textContent = btn.dataset.title || '';

      const add = card.querySelector('[data-add]');
      if (add) {
        const wasIn = add.getAttribute('aria-pressed') === 'true';
        const previous = add.dataset.variant;

        add.dataset.variant = btn.dataset.choiceId || '';
        add.dataset.price = btn.dataset.price || '0';
        add.dataset.image = btn.dataset.image || '';
        add.disabled = false;

        // Changing colour on a piece already in the bundle swaps it in place,
        // rather than leaving the old one in and quietly disagreeing with the
        // card the shopper is looking at.
        if (wasIn && index > -1) {
          const list = this.picks()[index] || [];
          const at = list.findIndex(function (pick) {
            return pick.variantId === previous;
          });
          if (at > -1) {
            list[at] = {
              variantId: add.dataset.variant,
              price: parseInt(add.dataset.price, 10) || 0,
              image: add.dataset.image,
              note: card.dataset.note || this.fallbackNote,
            };
          }
        }
      }

      this.showImage(card, btn.dataset.image);
      this.paint();
    }

    toggle(index, button, panel) {
      const list = this.picks()[index];
      const variantId = button.dataset.variant;
      if (!variantId) return;
      const max = this.allow(index);

      const at = list.findIndex(function (pick) {
        return pick.variantId === variantId;
      });

      if (at > -1) {
        // Tapping the same choice again clears it, so nothing is a dead end.
        list.splice(at, 1);
      } else {
        const card = button.closest('.bb__cardwrap');
        const entry = {
          variantId: variantId,
          price: parseInt(button.dataset.price, 10) || 0,
          image: button.dataset.image || '',
          note: (card && card.dataset.note) || this.fallbackNote,
        };

        // Full already: the oldest pick makes way. Ignoring the tap instead
        // would leave a step that silently stops responding.
        if (max === 1) list.length = 0;
        else if (list.length >= max) list.shift();

        list.push(entry);
      }

      this.paint();
    }

    // Crossfade rather than snap: decode the new photo first, so the card
    // never flashes an empty box mid-swap.
    showImage(card, url) {
      const img = card.querySelector('.bb__img');
      if (!img) return;

      const next = url || img.dataset.default;
      if (!next || next === img.getAttribute('src')) return;

      const preload = new Image();
      img.style.opacity = '0.35';
      preload.onload = function () {
        img.src = next;
        img.style.opacity = '';
      };
      preload.onerror = function () {
        img.style.opacity = '';
      };
      preload.src = next;
    }

    // Restores a card to whatever the bundle says about it. Only used on entry:
    // a bundle re-opened from the chooser has to show its committed colours
    // again, while a card mid-choice keeps the colour the shopper just tapped.
    repaintCard(card, index) {
      const list = this.picks()[index] || [];
      const choices = Array.prototype.slice.call(card.querySelectorAll('[data-choice]'));
      const add = card.querySelector('[data-add]');
      const value = card.querySelector('[data-optvalue]');

      let chosen = null;
      for (let i = 0; i < choices.length; i++) {
        const hit = list.some(function (pick) {
          return pick.variantId === choices[i].dataset.choiceId;
        });
        if (hit) {
          chosen = choices[i];
          break;
        }
      }

      // Nothing committed for this card: fall back to the first in-stock
      // option, which is the default the markup ships with. Resetting to
      // nothing would leave the card emptier than it was on first paint.
      if (!chosen && choices.length) {
        chosen =
          choices.filter(function (option) {
            return !option.disabled;
          })[0] || null;
      }

      choices.forEach(function (option) {
        option.setAttribute('aria-pressed', option === chosen ? 'true' : 'false');
      });

      if (choices.length && add) {
        add.dataset.variant = chosen ? chosen.dataset.choiceId : '';
        add.dataset.price = chosen ? chosen.dataset.price : '0';
        add.dataset.image = chosen ? chosen.dataset.image : '';
        add.disabled = !chosen;
      }
      if (value) value.textContent = chosen ? chosen.dataset.title : 'Choose one';

      this.showImage(card, chosen ? chosen.dataset.image : '');
    }

    /* ------------------------------------------------------------ screens */

    toChooser(animate) {
      this.bundle = -1;
      this.index = 0;
      this.steps = [];
      // Swaps the header's support line in for the step progress bar.
      this.classList.add('is-choosing');

      this.stages.forEach(function (stage) {
        stage.hidden = true;
      });
      if (this.buildView) this.buildView.classList.remove('is-on');
      if (this.chooseView) this.chooseView.classList.add('is-on');
      if (this.bar) this.bar.hidden = true;
      if (this.backBtn) this.backBtn.hidden = true;

      const counter = this.querySelector('[data-counter]');
      const title = this.querySelector('[data-title]');
      if (counter) counter.textContent = this.chooserEyebrow;
      if (title) title.textContent = this.heading;

      const progress = this.querySelector('[data-progress]');
      if (progress) progress.style.width = '0%';

      if (animate && this.chooseView) {
        const first = this.chooseView.querySelector('[data-choose]');
        if (first) first.focus({ preventScroll: true });
      }
    }

    enter(bundle, animate) {
      this.bundle = bundle;
      const stage = this.stage();
      if (!stage) return this.toChooser(false);
      this.classList.remove('is-choosing');

      this.stages.forEach(function (other) {
        other.hidden = other !== stage;
      });
      if (this.chooseView) this.chooseView.classList.remove('is-on');
      if (this.buildView) this.buildView.classList.add('is-on');
      if (this.bar) this.bar.hidden = false;

      this.steps = Array.prototype.slice.call(stage.querySelectorAll('[data-step]'));
      this.track = stage.querySelector('[data-track]');
      this.buildRail();

      // Redraw every card, since a bundle re-entered from the chooser has to
      // show the picks it was left with.
      const self = this;
      this.steps.forEach(function (panel, i) {
        panel.querySelectorAll('.bb__cardwrap').forEach(function (card) {
          self.repaintCard(card, i);
        });
      });

      this.go(0, !!animate);
    }

    // One segment per tier, each followed by its milestone chip. Built on entry
    // because the shape follows the bundle, not the picks.
    buildRail() {
      if (!this.rail) return;
      const stage = this.stage();
      if (!stage) return;

      const base = parseFloat(stage.dataset.percent) || 0;
      const bonusAt = parseInt(stage.dataset.bonusQty, 10) || 0;
      const bonusPct = parseFloat(stage.dataset.bonusPercent) || 0;

      let html =
        '<span class="bb__seg" data-seg="0"><i></i></span>' +
        '<span class="bb__chip2" data-chip="0">' + base + '%</span>';

      // A second rung only exists where there is a better rate to climb to.
      if (bonusAt > 0 && bonusPct > base) {
        html +=
          '<span class="bb__seg" data-seg="1"><i></i></span>' +
          '<span class="bb__chip2" data-chip="1">' + bonusPct + '%</span>';
      }

      this.rail.innerHTML = html;
      // Force the thumbnails to redraw for the bundle now in play.
      this.thumbSig = null;
    }

    back() {
      if (this.index > 0) return this.go(this.index - 1, true);
      if (this.stages.length > 1) this.toChooser(true);
    }

    go(index, focus) {
      this.index = index;

      if (this.track) {
        this.track.style.transform = 'translateX(-' + index * 100 + '%)';
      }

      this.steps.forEach(function (panel, i) {
        const hidden = i !== index;
        panel.setAttribute('aria-hidden', hidden ? 'true' : 'false');
        // Keep off-screen controls out of the tab order.
        panel.querySelectorAll('button').forEach(function (button) {
          if (hidden) {
            button.setAttribute('tabindex', '-1');
          } else {
            button.removeAttribute('tabindex');
          }
        });
      });

      const panel = this.steps[index];
      const stage = this.stage();

      if (panel) {
        panel.scrollTop = 0;

        const title = this.querySelector('[data-title]');
        if (title) title.textContent = panel.dataset.label || '';

        const counter = this.querySelector('[data-counter]');
        if (counter) {
          const name = (stage && stage.dataset.name) || '';
          const where = 'Step ' + (index + 1) + ' of ' + this.steps.length;
          counter.textContent =
            this.steps.length > 1 && name ? name + ' - ' + where : name || where;
        }

        if (focus) {
          // Move focus into the new step so keyboard and screen reader users
          // are not left behind on the button they just pressed.
          panel.setAttribute('tabindex', '-1');
          panel.focus({ preventScroll: true });
        }
      }

      // At step one Back returns to the chooser, unless there is nothing to
      // return to.
      if (this.backBtn) {
        this.backBtn.hidden = index === 0 && this.stages.length < 2;
      }

      this.paint();
    }

    paint() {
      if (this.bundle < 0) return;

      const self = this;
      const picks = this.picks();

      this.steps.forEach(function (panel, index) {
        const list = picks[index] || [];
        panel.querySelectorAll('[data-variant]').forEach(function (button) {
          // Guard the empty id, or a card with no colour chosen yet would
          // match nothing and everything at once.
          const on =
            !!button.dataset.variant &&
            list.some(function (pick) {
              return pick.variantId === button.dataset.variant;
            });
          button.setAttribute('aria-pressed', on ? 'true' : 'false');
          const label = button.querySelector('[data-add-label]');
          if (label) label.textContent = on ? 'Added' : 'Add';
        });
      });

      // Thumbnails show what is actually in the bundle, capped at three with
      // the remainder counted on the last one.
      const flat = [];
      picks.forEach(function (list) {
        list.forEach(function (pick) {
          flat.push(pick);
        });
      });

      if (this.slotHost) {
        // Key off the images, not the count. Keying off the count meant that
        // swapping one variant for another left the old thumbnail in place.
        const sig = flat
          .map(function (pick) {
            return pick.image;
          })
          .join('|');

        if (this.thumbSig !== sig) {
          this.thumbSig = sig;
          const shown = flat.slice(0, 3);
          const extra = flat.length - shown.length;
          this.slotHost.innerHTML = shown
            .map(function (pick, i) {
              const badge =
                extra > 0 && i === shown.length - 1
                  ? '<span class="bb__plus2">+' + extra + '</span>'
                  : '';
              return (
                '<span class="bb__thumb"><img src="' +
                pick.image +
                '" alt="">' +
                badge +
                '</span>'
              );
            })
            .join('');
        }
      }

      const needed = this.steps.reduce(function (sum, panel, i) {
        return sum + self.need(i);
      }, 0);
      const banked = this.steps.reduce(function (sum, panel, i) {
        return sum + Math.min((picks[i] || []).length, self.need(i));
      }, 0);

      const progress = this.querySelector('[data-progress]');
      if (progress) {
        progress.style.width = (needed ? (banked / needed) * 100 : 0) + '%';
      }

      const stage = this.stage();
      const base = parseFloat(stage.dataset.percent) || 0;
      const bonusAt = parseInt(stage.dataset.bonusQty, 10) || 0;
      const bonusPct = parseFloat(stage.dataset.bonusPercent) || 0;
      const tiered = bonusAt > 0 && bonusPct > base;

      const complete = this.complete();
      const rate = this.rate();
      const atBest = !tiered || flat.length >= bonusAt;
      const full = flat.reduce(function (sum, pick) {
        return sum + pick.price;
      }, 0);
      const due = Math.round(full * (1 - rate / 100));

      // Rail. Segment one tracks the minimum; segment two tracks the climb from
      // there to the better rate, so each rung is half the journey.
      const segs = this.rail ? this.rail.querySelectorAll('[data-seg]') : [];
      const chips = this.rail ? this.rail.querySelectorAll('[data-chip]') : [];
      const fillTo = function (seg, ratio) {
        const inner = seg && seg.querySelector('i');
        if (inner) inner.style.width = Math.max(0, Math.min(ratio, 1)) * 100 + '%';
      };
      if (segs[0]) fillTo(segs[0], needed ? banked / needed : 0);
      if (chips[0]) chips[0].classList.toggle('is-on', complete);
      if (tiered) {
        const span = bonusAt - needed;
        if (segs[1]) fillTo(segs[1], span > 0 ? (flat.length - needed) / span : 0);
        if (chips[1]) chips[1].classList.toggle('is-on', flat.length >= bonusAt);
      }

      const showSave = complete && rate > 0;
      const save = this.querySelector('[data-save]');
      if (save) {
        save.textContent = showSave ? 'Save ' + this.money(full - due) : '';
        save.hidden = !showSave;
      }

      const hint = this.querySelector('[data-hint]');
      if (hint) {
        let line = '';
        let won = false;
        if (!complete) {
          line = 'Pick ' + (needed - banked) + ' more to unlock ' + base + '% off';
        } else if (!atBest) {
          line = stage.dataset.bonusHint || '';
        } else {
          line = 'Best price unlocked, ' + rate + '% off';
          won = true;
        }
        hint.textContent = line;
        hint.classList.toggle('is-won', won);
      }

      const last = this.index === this.steps.length - 1;
      if (this.ctaLabel) {
        this.ctaLabel.textContent = last ? 'Add bundle to cart' : 'Next';
      }
      if (this.ctaPrice) {
        this.ctaPrice.textContent = full > 0 ? this.money(showSave ? due : full) : '';
        this.ctaPrice.hidden = full <= 0;
      }
      if (this.cta) {
        // Next needs only this step; the final button needs the whole bundle.
        this.cta.disabled = last ? !complete : !this.done(this.index);
      }

      // Every tier reached gets its own burst, keyed per bundle so each fires
      // once and not again on the way back from the chooser. Where one pick
      // would satisfy both at once only the higher one plays, so two bursts
      // can never land on the same frame.
      if (complete) {
        const topTier = tiered && bonusAt > needed && flat.length >= bonusAt;
        this.celebrate((topTier ? 'tier2-' : 'tier1-') + this.bundle);
      }
    }

    onCta() {
      if (this.cta.disabled) return;
      const last = this.index === this.steps.length - 1;

      if (!last) {
        this.interlude(
          function () {
            this.go(this.index + 1, true);
          }.bind(this),
        );
        return;
      }

      // The last step gets its message too. The cart request is fired first
      // and runs underneath it, so the pause buys the network round trip
      // rather than costing it.
      //
      // No confetti here: the tiers already celebrate, and a third burst on
      // the way out competes with the cart page loading underneath it.
      if (this.ctaLabel) this.ctaLabel.textContent = 'Adding...';
      if (this.ctaPrice) this.ctaPrice.hidden = true;
      this.cta.disabled = true;
      const sending = this.addToCart();

      this.interlude(
        function () {
          sending.then(
            function (error) {
              if (!error) {
                window.location.href = this.cartUrl;
                return;
              }
              this.cta.disabled = false;
              if (this.ctaLabel) this.ctaLabel.textContent = 'Add bundle to cart';
              const hint = this.querySelector('[data-hint]');
              if (hint) hint.textContent = error;
            }.bind(this),
          );
        }.bind(this),
      );
    }

    /* ------------------------------------------------------------ flourish */

    // Reuses the store's own confetti file, so this matches the one the sticky
    // banner already plays. If lottie is not around it loads it once; if that
    // fails the flow carries on, just without the party.
    celebrate(key) {
      if (!this.lottieUrl || this.fired[key]) return;
      this.fired[key] = true;

      const host = document.createElement('div');
      host.className = 'bb__confetti';
      document.body.appendChild(host);

      const play = function () {
        if (!window.lottie) return host.remove();
        try {
          const anim = window.lottie.loadAnimation({
            container: host,
            renderer: 'svg',
            loop: false,
            autoplay: true,
            path: this.lottieUrl,
          });
          // Several bursts can share a page now, so each clears up after itself.
          anim.addEventListener('complete', function () {
            host.remove();
          });
        } catch (error) {
          host.remove();
        }
      }.bind(this);

      if (window.lottie) return play();

      const lib = document.createElement('script');
      lib.src =
        'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie_light.min.js';
      lib.onload = play;
      lib.onerror = function () {
        host.remove();
      };
      document.head.appendChild(lib);
    }

    // A beat between steps: the line for what they just chose, on its own,
    // then it clears itself and the next category arrives. `then` always runs,
    // so a missing note can never strand someone mid-flow.
    interlude(then) {
      // The line belongs to the step just finished, read fresh rather than
      // remembered, so stepping back and forward cannot show a stale message.
      const list = this.picks()[this.index] || [];
      const recent = list[list.length - 1];
      const note = (recent && recent.note) || '';
      if (!note || !this.inter || !this.interText) return then();

      this.interText.textContent = note;
      this.inter.hidden = false;
      this.cta.disabled = true;

      requestAnimationFrame(
        function () {
          this.inter.classList.add('is-on');
        }.bind(this),
      );

      window.setTimeout(
        function () {
          this.inter.classList.remove('is-on');
          window.setTimeout(
            function () {
              this.inter.hidden = true;
              then();
            }.bind(this),
            380,
          );
        }.bind(this),
        this.interMs,
      );
    }

    onKey(event) {
      if (this.lb && !this.lb.hidden) {
        // The gallery owns the keyboard while it is up.
        if (event.key === 'Escape') this.closeGallery();
        if (event.key === 'ArrowLeft') this.showShot(this.shot - 1);
        if (event.key === 'ArrowRight') this.showShot(this.shot + 1);
        return;
      }
      if (event.key === 'Escape') this.close();
    }

    // Back to whatever they were browsing, or to the shop when the bundle page
    // is where they landed.
    //
    // history.length counts cross-origin entries too, so it reads > 1 for
    // someone who arrived straight from an ad or a search result. Stepping
    // back there would eject them from the store rather than return them to
    // it, so the previous page has to be ours before we go back.
    close() {
      let internal = false;
      try {
        internal =
          !!document.referrer &&
          new URL(document.referrer).origin === window.location.origin;
      } catch (error) {
        internal = false;
      }

      if (internal && window.history.length > 1) {
        window.history.back();
        return;
      }
      window.location.href = '/';
    }

    /* ----------------------------------------------------------- gallery */

    openGallery(shutter) {
      let shots = [];
      try {
        shots = JSON.parse(shutter.dataset.gallery || '[]');
      } catch (error) {
        shots = [];
      }
      if (!shots.length) return;

      this.shots = shots;
      this.opener = shutter;

      this.lbTrack.innerHTML = shots
        .map(function (url) {
          return '<img src="' + url + '" alt="" draggable="false">';
        })
        .join('');

      this.lbDots.innerHTML =
        shots.length > 1
          ? shots
              .map(function (url, i) {
                return (
                  '<button type="button" class="bb__lbdot" data-lb-dot="' +
                  i +
                  '" aria-label="Photo ' +
                  (i + 1) +
                  '"></button>'
                );
              })
              .join('')
          : '';

      this.lb.hidden = false;
      requestAnimationFrame(
        function () {
          this.lb.classList.add('is-open');
        }.bind(this),
      );

      // Start on the photo they were looking at, not always the first.
      const current = shutter.querySelector('.bb__img');
      const at = current ? shots.indexOf(current.getAttribute('src')) : -1;
      this.showShot(at > -1 ? at : 0, true);

      const close = this.lb.querySelector('[data-lb-close]');
      if (close) close.focus();
    }

    closeGallery() {
      if (!this.lb || this.lb.hidden) return;
      this.lb.classList.remove('is-open');

      const done = function () {
        this.lb.hidden = true;
        this.lbTrack.innerHTML = '';
        // Send focus back where it came from rather than to the top of the page.
        if (this.opener) this.opener.focus();
        this.opener = null;
      }.bind(this);

      window.setTimeout(done, 280);
    }

    showShot(index, instant) {
      const last = this.shots.length - 1;
      this.shot = Math.max(0, Math.min(index, last));

      if (instant) this.lbTrack.style.transition = 'none';
      this.lbTrack.style.transform = 'translateX(-' + this.shot * 100 + '%)';
      if (instant) {
        // Force a reflow so the transition-less jump is not batched with the
        // rule that puts the transition back.
        void this.lbTrack.offsetWidth;
        this.lbTrack.style.transition = '';
      }

      if (this.lbCap) {
        this.lbCap.textContent =
          this.shots.length > 1 ? this.shot + 1 + ' / ' + this.shots.length : '';
      }
      if (this.lbPrev) this.lbPrev.disabled = this.shot === 0;
      if (this.lbNext) this.lbNext.disabled = this.shot === last;

      const self = this;
      this.lbDots.querySelectorAll('[data-lb-dot]').forEach(function (dot, i) {
        dot.setAttribute('aria-current', i === self.shot ? 'true' : 'false');
      });
    }

    bindSwipe() {
      if (!this.lb) return;
      let startX = 0;
      let startY = 0;

      this.lb.addEventListener(
        'touchstart',
        function (event) {
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
        },
        { passive: true },
      );

      this.lb.addEventListener(
        'touchend',
        function (event) {
          const dx = event.changedTouches[0].clientX - startX;
          const dy = event.changedTouches[0].clientY - startY;
          // Ignore mostly-vertical drags so a scroll gesture is not a swipe.
          if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
          this.showShot(this.shot + (dx < 0 ? 1 : -1));
        }.bind(this),
        { passive: true },
      );
    }

    /* -------------------------------------------------------------- cart */

    money(cents) {
      const amount = (cents / 100).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      return this.moneyFormat.replace('{{amount}}', amount);
    }

    // Resolves to null on success, or to the message to show on failure.
    // Never redirects itself, so the caller decides when to leave the page.
    async addToCart() {
      // A shared stamp keeps the lines identifiable as one bundle in the cart
      // and on the order, and names which bundle it was.
      const stage = this.stage();
      const name = (stage && stage.dataset.name) || 'Bundle';
      const stamp = name + ' - ' + Date.now();

      const items = [];
      this.picks().forEach(function (list) {
        list.forEach(function (pick) {
          items.push({
            id: pick.variantId,
            quantity: 1,
            properties: { _bundle: stamp },
          });
        });
      });

      if (!items.length) return 'Nothing selected.';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items }),
        });

        if (!response.ok) {
          const body = await response.json().catch(function () {
            return {};
          });
          return body.description || body.message || 'Could not add the bundle.';
        }

        return null;
      } catch (error) {
        return error.message || 'Could not add the bundle.';
      }
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
})();
